/**
 * Table ⇄ Kanban, as a segmented control.
 *
 * The design puts this on all four list screens — leads, events, publishing
 * plan, ambassadors — so it lives here rather than being rebuilt per screen.
 *
 * Colours are taken from the design rather than inferred: active is indigo
 * `#000B38` on white text, inactive is white on `#4c5b82`, and the two halves
 * share one bordered container with no border between them.
 *
 * ## It disappears on a phone
 *
 * The design resolves every one of these to `viewMode === "table" || isMobile`
 * and hides the control below 760px. A kanban board is columns side by side —
 * five stages need roughly 1,100px, and a 375px screen answers that with a
 * horizontal scroll through boards you cannot see the shape of, which is the
 * one thing a kanban is for. So mobile gets the list, and no choice about it.
 */
import { useState } from 'react'
import { useIsMobile } from '@/shared/nav/useIsMobile'
import './ViewToggle.css'

/**
 * `calendar` is offered only where a screen passes `withCalendar` — today that
 * is Tasks alone. It joins the union rather than living in a second control
 * because decision 59 is about exclusivity: table, board and calendar are three
 * ways of showing the same tasks, and a switch beside a separate chip reads as
 * two things being active at once.
 */
export type ViewMode = 'table' | 'kanban' | 'calendar' | 'map'

interface Props {
  value: ViewMode
  onChange: (v: ViewMode) => void
  /** Named so screen readers hear which list this switches. */
  label: string
  /** Adds a third segment. Only a screen that has a calendar may pass it. */
  withCalendar?: boolean
  /**
   * Adds the map segment — decision 84.
   *
   * Passed by every list whose records have a place: leads, events,
   * ambassadors, shareholders, partners. Tasks, posts, issuances and transfers
   * do not, because a task has a person and a date and no location.
   */
  withMap?: boolean
}

/**
 * The view mode, with the mobile rule applied once rather than per screen.
 *
 * Returns `'table'` on a phone whatever is stored, and keeps the stored value
 * so that widening the window restores the board you had. The setter still
 * works on mobile — nothing can call it, because the control is not rendered —
 * which is why the override is on the way out rather than on the way in.
 */
export function useViewMode(initial: ViewMode = 'table'): [ViewMode, (v: ViewMode) => void] {
  const isMobile = useIsMobile()
  const [mode, setMode] = useState<ViewMode>(initial)
  return [isMobile ? 'table' : mode, setMode]
}

export function ViewToggle({
  value,
  onChange,
  label,
  withCalendar = false,
  withMap = false,
}: Props) {
  const isMobile = useIsMobile()
  // Rendered nowhere below 760px: it would offer a view that cannot be shown.
  if (isMobile) return null

  return (
    <div className="viewtoggle" role="group" aria-label={`${label} view`}>
      <button
        className={`viewtoggle__btn${value === 'table' ? ' viewtoggle__btn--on' : ''}`}
        aria-pressed={value === 'table'}
        onClick={() => onChange('table')}
      >
        ☰ Table
      </button>
      <button
        className={`viewtoggle__btn${value === 'kanban' ? ' viewtoggle__btn--on' : ''}`}
        aria-pressed={value === 'kanban'}
        onClick={() => onChange('kanban')}
      >
        ▦ Kanban
      </button>
      {withCalendar && (
        <button
          className={`viewtoggle__btn${value === 'calendar' ? ' viewtoggle__btn--on' : ''}`}
          aria-pressed={value === 'calendar'}
          onClick={() => onChange('calendar')}
        >
          ▤ Calendar
        </button>
      )}
      {withMap && (
        <button
          className={`viewtoggle__btn${value === 'map' ? ' viewtoggle__btn--on' : ''}`}
          aria-pressed={value === 'map'}
          onClick={() => onChange('map')}
        >
          ◎ Map
        </button>
      )}
    </div>
  )
}
